import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Card, Statistic, Row, Col, Space, Tooltip, message } from 'antd';
import { DownloadOutlined, FilePdfOutlined, FileExcelOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

const columns = [
    {
        title: 'Sensor Name',
        dataIndex: 'SensorCode',
        key: 'SensorCode',
        width: 150,
        fixed: 'left',
        render: (text) => (
            <span className="font-semibold text-blue-600">{text}</span>
        ),
    },
    {
        title: 'Date & Time',
        dataIndex: 'RefDate',
        key: 'RefDate',
        width: 180,
        render: (text) => (
            <span className="text-gray-700">{text}</span>
        ),
    },
    {
        title: 'Value 1',
        dataIndex: 'Value1',
        key: 'Value1',
        width: 120,
        align: 'right',
        render: (value, record) => {
            if (record.SensorCode.startsWith("TMP")) {
                return <span className="font-mono text-purple-600">{value ? `${parseFloat(value)}°C` : '-'}</span>
            } else if (record.SensorCode.startsWith("RPM")) {
                return <span className="font-mono text-green-600">{value ? `${parseFloat(value)} RPM` : '-'}</span>
            } else if (record.SensorCode.startsWith("LVL")) {
                return <span className="font-mono text-blue-600">{value ? `${parseFloat(value)}cm` : '-'}</span>
            } else if (record.SensorCode.startsWith("INC")) {
                return <span className="font-mono text-blue-600">{value ? `${parseFloat(value)}°` : '-'}</span>
            } else {
                return <span className="font-mono text-gray-600">{value ? parseFloat(value) : '-'}</span>
            }
        }
    },
    {
        title: 'Value 2',
        dataIndex: 'Value2',
        key: 'Value2',
        width: 120,
        align: 'right',
        render: (value, record) => {
            if (record.SensorCode.startsWith("TMP")) {
                return <span className="font-mono text-purple-600">{value ? `${parseFloat(value)}°C` : '-'}</span>
            } else if (record.SensorCode.startsWith("RPM")) {
                return <span className="font-mono text-green-600">{value ? `${parseFloat(value)} RPM` : '-'}</span>
            } else if (record.SensorCode.startsWith("LVL")) {
                return <span className="font-mono text-blue-600">{value ? `${parseFloat(value)}L` : '-'}</span>
            } else if (record.SensorCode.startsWith("INC")) {
                return <span className="font-mono text-blue-600">{value ? `${parseFloat(value)}°` : '-'}</span>
            } else {
                return <span className="font-mono text-gray-600">{value ? parseFloat(value) : '-'}</span>
            }
        }
    },
    {
        title: 'Value 3',
        dataIndex: 'Value3',
        key: 'Value3',
        width: 120,
        align: 'right',
        render: (value) => (
            <span className="font-mono text-purple-600">{value ? parseFloat(value) : '-'}</span>
        ),
    },
];

const sensorCodeMapping = (code) => {
    switch (code) {
        case 'RPM001':
            return 'RPM 1';
        case 'RPM002':
            return 'RPM 2';
        case 'TMP001':
            return 'Temperatur 1';
        case 'TMP002':
            return 'Temperatur 2';
        case 'LVL001':
            return 'Level';
        case 'INC001':
            return 'Inclino';
        default:
            return code;
    }
};

const DataTables = ({ DataSource }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [dataValue, setDataValue] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = DataSource;
            const formattedResponse = response.map(item => ({
                ...item,
                RefDate: new Date(item.RefDate).toISOString().replace("T", " ").replace(".000Z", ""),
                SensorCode: sensorCodeMapping(item.SensorCode),
            }));
            setDataValue(formattedResponse.reverse());
        } catch (error) {
            setDataValue([]);
            console.log(error);
        }
        setIsLoading(false);
    }, [DataSource]);

    const fetchDataLoad = useCallback(async () => {
        try {
            const response = DataSource;
            const formattedResponse = response.map(item => ({
                ...item,
                RefDate: new Date(item.RefDate).toISOString().replace("T", " ").replace(".000Z", ""),
                SensorCode: sensorCodeMapping(item.SensorCode),
            }));
            setDataValue(formattedResponse.reverse());
        } catch (error) {
            setDataValue([]);
            console.log(error);
        }
    }, [DataSource]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(() => {
            fetchDataLoad();
        }, 300000);

        return () => {
            clearInterval(interval);
        };
    }, [fetchData, fetchDataLoad]);

    // Calculate statistics
    const getStatistics = () => {
        if (dataValue.length === 0) return { totalRecords: 0, uniqueSensors: 0, dateRange: 'N/A' };

        const uniqueSensors = new Set(dataValue.map(item => item.SensorCode)).size;
        const dates = dataValue.map(item => new Date(item.RefDate)).sort();
        const dateRange = dates.length > 0
            ? `${dayjs(dates[0]).format('DD MMM YYYY')} - ${dayjs(dates[dates.length - 1]).format('DD MMM YYYY')}`
            : 'N/A';

        return {
            totalRecords: dataValue.length,
            uniqueSensors,
            dateRange
        };
    };

    const stats = getStatistics();

    const exportPDF = async () => {
        setExportLoading(true);
        try {
            const doc = new jsPDF('landscape');

            // Add header
            doc.setFontSize(20);
            doc.text('Sensor Data History Report', 14, 22);

            doc.setFontSize(12);
            doc.text(`Generated on: ${dayjs().format('DD MMMM YYYY, HH:mm:ss')}`, 14, 30);
            doc.text(`Total Records: ${stats.totalRecords}`, 14, 38);
            doc.text(`Date Range: ${stats.dateRange}`, 14, 46);

            // Add table
            const tableColumn = columns.map(col => col.title);
            const tableRows = dataValue.map(item => columns.map(col => item[col.dataIndex]));

            doc.autoTable({
                head: [tableColumn],
                body: tableRows,
                startY: 55,
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245],
                },
                margin: { top: 55 },
            });

            doc.save(`Sensor_History_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
            message.success('PDF exported successfully!');
        } catch (error) {
            console.error('PDF export error:', error);
            message.error('Failed to export PDF');
        } finally {
            setExportLoading(false);
        }
    };

    const exportXLSX = async () => {
        setExportLoading(true);
        try {
            const worksheet = XLSX.utils.json_to_sheet(dataValue);
            const workbook = XLSX.utils.book_new();

            // Add metadata
            const metadata = [
                ['Sensor Data History Report'],
                ['Generated on:', dayjs().format('DD MMMM YYYY, HH:mm:ss')],
                ['Total Records:', stats.totalRecords],
                ['Unique Sensors:', stats.uniqueSensors],
                ['Date Range:', stats.dateRange],
                [''],
                ['Data:']
            ];

            const metadataSheet = XLSX.utils.aoa_to_sheet(metadata);
            XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Report Info');
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Sensor Data');

            XLSX.writeFile(workbook, `Sensor_History_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);
            message.success('Excel file exported successfully!');
        } catch (error) {
            console.error('Excel export error:', error);
            message.error('Failed to export Excel file');
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Statistics Cards */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                    <Card className="h-full">
                        <Statistic
                            title="Total Records"
                            value={stats.totalRecords}
                            prefix={<InfoCircleOutlined className="text-blue-500" />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className="h-full">
                        <Statistic
                            title="Unique Sensors"
                            value={stats.uniqueSensors}
                            prefix={<InfoCircleOutlined className="text-green-500" />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className="h-full">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600 mb-1">
                                <InfoCircleOutlined className="mr-2" />
                                Date Range
                            </div>
                            <div className="text-gray-600 text-sm">{stats.dateRange}</div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Export Controls */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                            <DownloadOutlined className="text-white text-lg" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Export Data</h3>
                            <p className="text-sm text-gray-600">Download your data in various formats</p>
                        </div>
                    </div>

                    <Space wrap>
                        <Tooltip title="Export as PDF">
                            <Button
                                type="primary"
                                icon={<FilePdfOutlined />}
                                onClick={exportPDF}
                                loading={exportLoading}
                                className="bg-red-500 hover:bg-red-600 border-red-500 hover:border-red-600"
                            >
                                PDF
                            </Button>
                        </Tooltip>

                        <Tooltip title="Export as Excel">
                            <Button
                                type="primary"
                                icon={<FileExcelOutlined />}
                                onClick={exportXLSX}
                                loading={exportLoading}
                                className="bg-green-500 hover:bg-green-600 border-green-500 hover:border-green-600"
                            >
                                Excel
                            </Button>
                        </Tooltip>

                        <Tooltip title="Refresh Data">
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchData}
                                loading={isLoading}
                            >
                                Refresh
                            </Button>
                        </Tooltip>
                    </Space>
                </div>
            </Card>

            {/* Data Table */}
            <Card className="overflow-hidden">
                <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Sensor Data Table</h3>
                    <p className="text-sm text-gray-600">
                        Showing {dataValue.length} records with real-time sensor readings
                    </p>
                </div>

                <Table
                    loading={isLoading}
                    columns={columns}
                    dataSource={dataValue}
                    rowKey={(record, index) => `${record.SensorCode}-${record.RefDate}-${index}`}
                    scroll={{
                        x: 800,
                        y: 400,
                    }}
                // pagination={{
                //     pageSize: 20,
                //     showSizeChanger: true,
                //     showQuickJumper: true,
                //     showTotal: (total, range) =>
                //         `${range[0]}-${range[1]} of ${total} records`,
                //     pageSizeOptions: ['10', '20', '50', '100'],
                // }}
                // className="custom-table"
                // size="middle"
                />
            </Card>
        </div>
    );
};

export default DataTables;
